import React, { useState, useEffect, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { AuthContext, AuthProvider } from './context/AuthContext';
// Components and pages imports
import BarcodeGenerator from './components/BarcodeGenerator';
import CreateOrder from "./components/CreateOrder"
import CreateProduct from "./components/CreateProduct"
import Products from "./pages/Products"
import ProductDetail from "./components/ProductDetail"
import ProductEdit from "./components/ProductEdit"
import OrderSuccess from './components/OrderSuccess';
import OrderList from './components/OrderList';
import OrderDetail from './components/OrderDetail';
import VendorPool from './components/VendorPool';
import ColorPool from './pages/ColorPool';
import CreateColor from './pages/CreateColor';
import EditColor from './pages/EditColor';
import PrintBarcodePage from './components/PrintBarcodePage';
import DesignerPanel from './pages/DesignerPanel';
import FabricPool from './pages/FabricPool';
import CreateFabric from './pages/CreateFabric';
import FabricDetail from './pages/FabricDetail';
import EditFabric from './pages/EditFabric';
import AccessoryPool from './pages/AccessoryPool';
import CreateAccessory from './pages/CreateAccessory';
import SourcingPanel from './pages/SourcingPanel';
import ModelhanePanel from './pages/ModelhanePanel';
import PlanlamaPanel from './pages/PlanlamaPanel';
import AccessoryDetail from './pages/AccessoryDetail';
import EditAccessory from './pages/EditAccessory';
import AccessoryImageTest from './pages/AccessoryImageTest';
import AccessoryDebugger from './pages/AccessoryDebugger';
import ManageUsers from './pages/ManageUsers';
import './index.css';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Unauthorized from './pages/Unauthorized';
import Sidebar from './components/Sidebar'; // Yeni Sidebar komponentini import et
import Dashboard from './pages/Dashboard';
import BarcodeGeneratorPage from './pages/BarcodeGeneratorPage';
import ProductCodeGenerator from './pages/ProductCodeGenerator';

// Yeni sayfaları import et
import TechnicalSheetList from './pages/TechnicalSheetList';
import TechnicalSheetDetail from './pages/TechnicalSheetDetail';
import TechnicalSheetEdit from './pages/TechnicalSheetEdit';
import RolesPage from './pages/RolesPage'; // Yeni sayfayı import edin
import UserRolesPage from './pages/UserRolesPage'; // Yeni sayfayı import edin
import SizeSpecificationsPage from './pages/SizeSpecificationsPage'; // Yeni sayfayı import et
import SizeSpecDetailPage from './pages/SizeSpecDetailPage'; // Yeni sayfayı import et
import UiBrief from './components/UiBrief'; // UiBrief bileşenini içe aktar
import MaterialList from './pages/MaterialList';
import FitKritikPage from './pages/FitKritikPage';
import FitKritikDetayPage from './pages/FitKritikDetayPage';
import FitKritikEditPage from './pages/FitKritikEditPage';

// NavLink bileşeni - izin kontrolü ile güncellendi
const NavLink = ({ 
  to, 
  children, 
  className, 
  resource,
  action = 'read',
  ...props 
}: { 
  to: string, 
  children: React.ReactNode, 
  className?: string, 
  resource?: string,
  action?: string,
  [key: string]: any 
}) => {
  const location = useLocation();
  const { hasPermission, isAdmin } = useContext(AuthContext);
  const isActive = location.pathname === to || (to !== '/' && location.pathname.startsWith(to));
  
  // Resource belirtilmişse ve erişim yoksa null döndür (gösterme)
  if (resource && !isAdmin && !hasPermission(resource, action)) {
    return null;
  }
  
  return (
    <Link 
      to={to} 
      className={`${className} ${isActive ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:bg-gray-100'}`}
      {...props}
    >
      {children}
    </Link>
  );
};

const AppContent: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { user, logout, isAdmin, hasPermission } = useContext(AuthContext);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      setSidebarOpen(window.innerWidth >= 1024);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const toggleProfileMenu = () => setProfileMenuOpen(!profileMenuOpen);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col">
      {/* Top navigation */}
      <nav className="bg-white shadow-lg w-full z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              
              {/* Mobile menu button */}
              {isMobile && (
                <button 
                  onClick={toggleSidebar} 
                  className="text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 mr-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              )}
              <div className="flex-shrink-0 flex items-center">
                <span className="text-xl font-semibold text-gray-800">Baleca</span>
              </div>
            </div>
            
            {/* User profile */}
            <div className="flex items-center relative">
              {user && (
                <span className="hidden md:inline-block text-sm font-medium text-gray-600 mr-3">
                  {user.username} {user.isAdmin ? '(Admin)' : ''}
                </span>
              )}
              
              <button 
                onClick={toggleProfileMenu}
                className="flex text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <img 
                  className="h-8 w-8 rounded-full object-cover"
                  src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                  alt="Kullanıcı profili" 
                />
              </button>
              
              {profileMenuOpen && (
                <div className="origin-top-right absolute right-0 mt-12 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 z-50">
                  <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-200">
                    Merhaba, {user?.username || 'Kullanıcı'}
                  </div>
                  <Link 
                    to="/profile" 
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setProfileMenuOpen(false)}
                  >
                    Profil
                  </Link>
                  <button 
                    onClick={logout}
                    className="w-full text-left block px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    Çıkış Yap
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Content area with sidebar and main content */}
      <div className="flex flex-1">
        {/* Sidebar */}
        <Sidebar 
          isMobile={isMobile}
          sidebarOpen={sidebarOpen}
          toggleSidebar={toggleSidebar}
        />

        {/* Main content */}
        <div className="flex-1">
          {/* Page content */}
          <main className="flex-1 p-4 sm:p-6">
            <Routes>
              {/* Ana sayfa */}
              <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/barcode-generator" element={<ProtectedRoute><BarcodeGeneratorPage /></ProtectedRoute>} />
                            
              
              {/* Sipariş yönetimi */}
              <Route path="/create-order" element={<ProtectedRoute resource="orders" action="create"><CreateOrder /></ProtectedRoute>} />
              <Route path="/orders" element={<ProtectedRoute resource="orders" action="read"><OrderList /></ProtectedRoute>} />
              <Route path="/order-detail/:orderNumber" element={<ProtectedRoute resource="orders" action="read"><OrderDetail /></ProtectedRoute>} />
              <Route path="/order-success" element={<ProtectedRoute resource="orders" action="read"><OrderSuccess /></ProtectedRoute>} />
              
              {/* Ürün yönetimi */}
              <Route path="/create-product" element={<ProtectedRoute resource="products" action="create"><CreateProduct /></ProtectedRoute>} />
              <Route path="/products" element={<ProtectedRoute resource="products" action="read"><Products /></ProtectedRoute>} />
              <Route path="/products/:id" element={<ProtectedRoute resource="products" action="read"><ProductDetail /></ProtectedRoute>} />
              <Route path="/products/:id/edit" element={<ProtectedRoute resource="products" action="update"><ProductEdit /></ProtectedRoute>} />
              
              {/* Tedarikçi yönetimi */}
              <Route path="/vendors" element={<ProtectedRoute resource="vendors" action="read"><VendorPool /></ProtectedRoute>} />
              
              {/* Renk yönetimi */}
              <Route path="/colors" element={<ProtectedRoute resource="colors" action="read"><ColorPool /></ProtectedRoute>} />
              <Route path="/colors/create" element={<ProtectedRoute resource="colors" action="create"><CreateColor /></ProtectedRoute>} />
              <Route path="/colors/:id/edit" element={<ProtectedRoute resource="colors" action="update"><EditColor /></ProtectedRoute>} />
              
              {/* Barkod yazdırma */}
              <Route path="/print-barcodes" element={<ProtectedRoute><PrintBarcodePage /></ProtectedRoute>} />
              
              {/* Departman panelleri */}
              <Route path="/designer" element={<ProtectedRoute resource="designer" action="read"><DesignerPanel /></ProtectedRoute>} />
              <Route path="/sourcing" element={<ProtectedRoute resource="sourcing" action="read"><SourcingPanel /></ProtectedRoute>} />
              <Route path="/modelhane" element={<ProtectedRoute resource="modelist" action="read"><ModelhanePanel /></ProtectedRoute>} />
              <Route path="/planlama" element={<ProtectedRoute resource="planlama" action="read"><PlanlamaPanel /></ProtectedRoute>} />
              
              {/* Kumaş yönetimi */}
              <Route path="/fabrics" element={<ProtectedRoute resource="fabrics" action="read"><FabricPool /></ProtectedRoute>} />
              <Route path="/fabrics/create" element={<ProtectedRoute resource="fabrics" action="create"><CreateFabric /></ProtectedRoute>} />
              <Route path="/fabrics/:id" element={<ProtectedRoute resource="fabrics" action="read"><FabricDetail /></ProtectedRoute>} />
              <Route path="/fabrics/:id/edit" element={<ProtectedRoute resource="fabrics" action="update"><EditFabric /></ProtectedRoute>} />
              
              {/* Aksesuar yönetimi */}
              <Route path="/accessories" element={<ProtectedRoute resource="accessories" action="read"><AccessoryPool /></ProtectedRoute>} />
              <Route path="/accessories/create" element={<ProtectedRoute resource="accessories" action="create"><CreateAccessory /></ProtectedRoute>} />
              <Route path="/accessories/:id" element={<ProtectedRoute resource="accessories" action="read"><AccessoryDetail /></ProtectedRoute>} />
              <Route path="/accessories/:id/edit" element={<ProtectedRoute resource="accessories" action="update"><EditAccessory /></ProtectedRoute>} />
              <Route path="/accessories/image-test" element={<ProtectedRoute resource="accessories" action="read"><AccessoryImageTest /></ProtectedRoute>} />
              <Route path="/accessories/debug" element={<ProtectedRoute resource="accessories" action="read"><AccessoryDebugger /></ProtectedRoute>} />
              
              {/* Teknik Föy rotaları */}
              <Route path="/technical-sheet" element={<ProtectedRoute resource="designer" action="read"><TechnicalSheetList /></ProtectedRoute>} />
              <Route path="/technical-sheet/:productId" element={<ProtectedRoute resource="designer" action="read"><TechnicalSheetDetail /></ProtectedRoute>} />
              <Route path="/technical-sheet/:productId/edit" element={<ProtectedRoute resource="designer" action="update"><TechnicalSheetEdit /></ProtectedRoute>} />
              <Route path="/materials" element={<ProtectedRoute resource="sourcing" action="read"><MaterialList /></ProtectedRoute>} />

              {/* Yeni Ölçü Tabloları Rotası */}
              <Route path="/size-specifications" element={<ProtectedRoute resource="products" action="read"><SizeSpecificationsPage /></ProtectedRoute>} />
              {/* Yeni Ölçü Tablosu Detay Rotası */}
              <Route path="/size-spec-detail/:productId" element={<ProtectedRoute resource="products" action="read"><SizeSpecDetailPage /></ProtectedRoute>} />

              {/* Fit Kritik */}
              <Route path="/fit" element={<ProtectedRoute resource="fitcrt" action="read"><FitKritikPage /></ProtectedRoute>} />
              <Route path="/fit/:productId" element={<ProtectedRoute resource="fitcrt" action="read"><FitKritikDetayPage /></ProtectedRoute>} />
              <Route path="/fit/:productId/edit" element={<ProtectedRoute resource="fitcrt" action="update"><FitKritikEditPage /></ProtectedRoute>} />

              {/* Admin sayfaları */}
              <Route path="/manage-users" element={<ProtectedRoute resource="admin" action="read"><ManageUsers /></ProtectedRoute>} />
              <Route path="/roles" element={<ProtectedRoute resource="admin" action="read"><RolesPage /></ProtectedRoute>} />
              <Route path="/user-roles" element={<ProtectedRoute resource="admin" action="read"><UserRolesPage /></ProtectedRoute>} />
              <Route path="/product-codes" element={<ProtectedRoute resource="admin" action="read"><ProductCodeGenerator /></ProtectedRoute>} />
              <Route path="/ui-brief" element={<ProtectedRoute><UiBrief /></ProtectedRoute>} />
              <Route path="/page-not-found" element={<div>404 - Sayfa Bulunamadı</div>} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/*" element={<ProtectedRoute><AppContent /></ProtectedRoute>} />
        </Routes>
      </AuthProvider>
    </Router>
  );
};

export default App;
